import React from 'react';

import { useRouter } from 'next/router';

import { I18N_NAMESPACES } from 'i18n';
import { useTranslation } from 'i18n/hooks';
import { Spinner } from 'revibe-ui';

import { Layout } from 'modules/common/components/Layout';
import { ShopPage } from 'modules/common/components/ShopPage';
import { ItemsGridWithFilters } from 'modules/item/components/ItemsGridWithFilters';
import { ITEM_FILTERS } from 'modules/item/utils/itemFilter';
import { useFiltersQuery } from 'modules/catalog/hooks';
import { buildItemsListingRoute } from 'modules/catalog/utils';
import { useCategories } from 'modules/item/hooks/useCategories';
import { MarketplaceAPI } from 'revibe-api';
import { NewsletterSubscription } from 'modules/user/components/NewsletterSubscription';

export const ItemsListingPage = () => {
  const { tu } = useTranslation('shop.listing');
  const router = useRouter();
  const { filters, filtersToken, filtersQueryIsLoading } = useFiltersQuery();
  const { categories } = useCategories();
  const handleFilterApply = (newFilters: MarketplaceAPI.ItemFilters) => {
    const url = buildItemsListingRoute(
      newFilters[ITEM_FILTERS.SEX] || filters[ITEM_FILTERS.SEX],
      newFilters[ITEM_FILTERS.CATEGORY] || filters[ITEM_FILTERS.CATEGORY],
      newFilters
    );
    router.push(url);
  };

  const title =
    filters[ITEM_FILTERS.ONLY_SALES] === 'true'
      ? tu('sales', 'shop')
      : `${tu(filters[ITEM_FILTERS.SEX], 'common')} ${
          filters[ITEM_FILTERS.CATEGORY] ? '-' : ''
        } ${
          filters[ITEM_FILTERS.CATEGORY]
            ? tu(filters[ITEM_FILTERS.CATEGORY], 'shop.categories')
            : ''
        }`;

  if (router.isFallback) {
    return (
      <ShopPage isShopPage title={title}>
        <Layout minHeight>
          <Spinner />
        </Layout>
      </ShopPage>
    );
  }

  return (
    <ShopPage
      isShopPage
      title={title}
      description={`${
        categories.find((c) => c.slug === filters[ITEM_FILTERS.CATEGORY])
          ?.description
      } - ${tu(filters[ITEM_FILTERS.SEX], 'common')} ${
        filters[ITEM_FILTERS.CATEGORY] ? '-' : ''
      } ${
        filters[ITEM_FILTERS.CATEGORY]
          ? tu(filters[ITEM_FILTERS.CATEGORY], 'shop.categories')
          : ''
      }`}
    >
      {filters[ITEM_FILTERS.ONLY_SALES] === 'true' ? (
        <div className="mx-auto grid gap-4 px-4 pt-8 md:grid-cols-2 lg:pt-10">
          <div className="text-center md:text-left">
            <h1 className="font-formula text-3xl uppercase md:text-4xl lg:text-5xl">
              {tu('sales.heading', I18N_NAMESPACES.LISTING)}
            </h1>
            <p className="max-w-3xl">
              {tu('sales.text', I18N_NAMESPACES.LISTING)}
            </p>
          </div>
          <NewsletterSubscription />
        </div>
      ) : (
        ['female', 'male', 'unisex'].includes(filters[ITEM_FILTERS.SEX]) && (
          <div className="mx-auto px-4 pt-8 text-center md:text-left lg:pt-10">
            <h1 className="font-formula text-3xl uppercase md:text-4xl lg:text-5xl">
              {tu(filters[ITEM_FILTERS.SEX], 'common')}{' '}
              {filters[ITEM_FILTERS.CATEGORY] && '/'}{' '}
              {tu(filters[ITEM_FILTERS.CATEGORY], 'shop.categories')}
            </h1>
            <p className="max-w-3xl">
              {
                categories.find(
                  (c) => c.slug === filters[ITEM_FILTERS.CATEGORY]
                )?.description
              }
            </p>
          </div>
        )
      )}
      <div className="pt-8 lg:pt-10">
        {filtersQueryIsLoading ? null : (
          <ItemsGridWithFilters
            list={`${filters[ITEM_FILTERS.SEX]} / ${
              filters[ITEM_FILTERS.CATEGORY]
            }`}
            filters={filters}
            onFiltersApplied={handleFilterApply}
            resetToken={filtersToken}
          />
        )}
      </div>
    </ShopPage>
  );
};
